import Backbone from 'backbone';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {Permissions} from 'auth/PermissionConstants';
import {NucleotideType} from 'biojs/Util';
import {NumberIDFolder} from 'constants/FolderConstants';
import {Strand, StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import {User} from 'constants/UserConstants';
import {BioentitySummary, Tag} from 'folder-items/FileConstants';
import {SequenceType as GQLSequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Note} from 'notebook/util/NoteUtilConstants';
import {SchemaValidationState} from 'schemas/SchemaValidationConstants';

// Keep in sync with benchling/molbio/sequences/models/sequence.py
export const SEQUENCE_MAX_LENGTH = 10000000;
export const OLIGO_MAX_LENGTH = 2000;

// Keep in sync with benchling/molbio/sequences/models/core/size_limits.py
export const ANNOTATION_MAX_NOTES_LENGTH = 10000;
export const ANNOTATION_MAX_NAME_LENGTH = 255;
export const ANNOTATION_MAX_TYPE_LENGTH = 255;
export const ANNOTATION_MAX_CUSTOM_FIELDS = 100;
export const TRANSLATION_MAX_NOTES_LENGTH = 10000;
export const TRANSLATION_MAX_NAME_LENGTH = 2048;
export const TRANSLATION_MAX_NUM_CUSTOM_FIELDS = 100;

export const CopyPreferences = {
  PRIMER_BASES: 'copy.PRIMER_BASES',
  ANNOTATIONS: 'copy.ANNOTATIONS',
  ANNOTATIONS_NOT_CONTAINED: 'copy.ANNOTATIONS_NOT_CONTAINED',
  TAGS: 'copy.TAGS',
  DESCRIPTION: 'copy.DESCRIPTION',
};

export const SequencePreferences = {
  SHOULD_COLLAPSE_PARTS_WARNING: 'SHOULD_COLLAPSE_PARTS_WARNING',
};

export enum SequenceType {
  // Long double-stranded DNA.
  DOUBLE_STRANDED = 'ds',
  // Long single-stranded RNA.
  SINGLE_STRANDED = 'ss',
  // Short single-stranded DNA or RNA.
  OLIGO = 'oligo',
}

// This conversion is essentially a noop since the enum values are identical, but it makes the type checker happy.
export const SEQUENCE_TYPE_GQL_TO_NATIVE_ENUM_MAP: Record<GQLSequenceType, SequenceType> = {
  [GQLSequenceType.ds]: SequenceType.DOUBLE_STRANDED,
  [GQLSequenceType.ss]: SequenceType.SINGLE_STRANDED,
  [GQLSequenceType.oligo]: SequenceType.OLIGO,
};
export const SEQUENCE_TYPE_NATIVE_ENUM_TO_GQL_MAP: Record<SequenceType, GQLSequenceType> = {
  [SequenceType.DOUBLE_STRANDED]: GQLSequenceType.ds,
  [SequenceType.SINGLE_STRANDED]: GQLSequenceType.ss,
  [SequenceType.OLIGO]: GQLSequenceType.oligo,
};

interface ServerSequenceAnalysis {}
// TODO: unify with AnnotationAttributes?
export interface ServerAnnotation {
  annotation_type: string;
  color: string;
  end: number;
  id: number;
  length: number;
  name: string;
  start: number;
  strand: Strand;
  notes: string;
  custom_fields: unknown;
}
interface ServerAssembly {}
export interface ServerFragmentResult {
  end: number | null;
  end_enzyme_id: number | null;
  end_sticky_end: string | null;
  fragment_id: number | null;
  index: number | null;
  original_bases: string | null;
  parent: {id: string; name: string} | null;
  parent_version: {createdAt: string; id: number} | null;
  primer_pair_id: number | null;
  start: number | null;
  start_enzyme_id: number | null;
  start_sticky_end: string | null;
  version_id?: number | null;
}
// TODO: unify with PartAttributes?
export interface ServerPart {
  complement: boolean;
  id: number;
  position: number;
  raw_bases: string;
  reverse: boolean;
  sequence: SequenceServerResponse | null;
  sequence_exists?: boolean;
  sequence_fresh?: boolean;
}
interface ServerPrimerPair {}
interface ServerSequencePrimer {}
interface ServerTagSchema {}
interface ServerTranslationGroup {}
interface ServerVersion {}

export interface ModifiedNucleotideAttributes {
  position: number;
  sugarMonomerId: string;
  phosphateMonomerId: string | null;
  baseMonomerId: string;
}

export interface ServerTranslation {
  id: number;
  translationGroupId: number | null;
  start: number;
  end: number;
  strand: StrandOfDoubleStranded;
}

// TODO unify with SequenceAttributes and/or generated GQL types?
export interface SequenceServerResponse {
  analyses: Array<ServerSequenceAnalysis>;
  annotations: Array<ServerAnnotation>;
  archiveRecord: ArchiveRecord | null;
  assembly: ServerAssembly | null;
  authors: Array<User>;
  bases?: string;
  bioentityAliasInfos: Array<BioentityAliasInfo>; // TODO verify
  circular: boolean;
  color: string;
  created_at: string;
  creator: string;
  displayLabel: string;
  editURL: string;
  finalized: boolean;
  folder: NumberIDFolder;
  fragment_results: Array<ServerFragmentResult>;
  hasSnapshot: boolean;
  id: string;
  latestVersionId: number;
  length: number;
  locked: boolean;
  modified_at: string;
  modified_nucleotides: Array<ModifiedNucleotideAttributes>;
  name: string;
  note: Note; // TODO verify
  nucleotideType: NucleotideType;
  owner: string;
  parts: Array<ServerPart>;
  permissions: Permissions; // TODO verify
  primer_pairs: Array<ServerPrimerPair>;
  schemaValidationState: SchemaValidationState | null;
  sequence_primers: Array<ServerSequencePrimer>;
  sequence_type: SequenceType;
  snapshot_url: string;
  startIndex: number;
  tagSchema: ServerTagSchema | null;
  tags: Array<Tag>;
  translationGroups: Array<ServerTranslationGroup>;
  translations: Array<ServerTranslation>;
  type: SequenceType;
  versions: Array<ServerVersion>;
  writable: boolean;
}

export interface SequenceSummaryWithoutPermissions extends BioentitySummary {
  // Bases is only included for oligos and DNA with "show_residues" on the tag schema.
  bases?: string;
  circular: boolean;
  color: string;
  hasSnapshot: boolean;
  latestVersionId: number | null;
  length: number;
  locked: boolean;
  modified_at: string;
  nucleotideType: NucleotideType;
  owner: string;
  projectFolderId?: string;
  sequence_type: SequenceType;
  snapshot_url: string;
  startIndex: number;
  type: SequenceType;
}

export interface SequenceSummary extends SequenceSummaryWithoutPermissions {
  permissions: Permissions;
}

export interface SequenceVersion {
  id: number;
  name: string;
  created_at: string;
  parentId: number | null;
}

export interface PolymerFeatureAttributes {
  // Typicaly id is left off of these interfaces since we never update them. When a PolymerFeatureModel is hoisted
  // from a part though, the id does get updated to avoid incorrect de-duplication.
  id?: string | number | null;
  start: number;
  end: number;
  fromSubPart?: boolean;
  strand: Strand;
  bind_position?: number;
}

export interface PolymerFeatureModel extends Backbone.Model {
  get<K extends keyof PolymerFeatureAttributes>(key: K): PolymerFeatureAttributes[K];
  set<K extends keyof PolymerFeatureAttributes>(
    attributeName: K,
    value: PolymerFeatureAttributes[K],
    options?: Backbone.ModelSetOptions
  ): PolymerFeatureModel;
  set(obj: Partial<PolymerFeatureAttributes>): PolymerFeatureModel;

  start(): number;
  end(): number;
  fromSubPart(): boolean;
  strand(): Strand;
  clone(): this;
  clone(offset: number): this;
  identityFingerprint(): string;
}

export interface MatchSite {
  matchStart: number;
  matchEnd: number;
  enzymeName: string;
  recognitionSite: string;
  enzymeId: number;
}

export interface CutPosition {
  position: number;
  strand: number;
}

export interface EnzymeActivity {
  buffer: string;
  highlighted: boolean;
  value: string;
}

export interface ProvidedEnzyme {
  activityRows: Array<Array<EnzymeActivity>>;
  baseName: string;
  inactivationTemperature: number;
  incubationTemperature: number;
  url: string | null;
}

export interface FragmentResult {
  color: string;
  name: string;
  info: string;
  version: string;
}

export interface SequenceAnalysis {
  id: string;
  name: string;
  // TODO(TypeScript): Fill in other fields.
}

// Keep in sync with SequenceTopology in benchling/pybio/util.py
export enum SequenceTopology {
  // TODO (BNCH-61703): Make uppercase and use everywhere
  CIRCULAR = 'circular',
  LINEAR = 'linear',
}
